import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Table from '@awp/components/table';
import { toast } from 'react-toastify';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { Button, Row, Col, FormLabel, Accordion, Card } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { DeleteJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API, Usertypes } from '@awp/constants';
import { hasAccess } from '@components/Authorize';
import AwpDropdown from '@awp/components/Common/DropDown/AwpDropdown';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { selectCurrentUser } from '@stores/usersSlice';
import { selectUserTypes } from '@awp/stores/dropdownsSlice';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import { Crud } from '@awp/shared/Crud';

const ForecastRecipientTable = ({
  isMedium,
  isLarge,
  setData,
  data,
  filteredData,
  setFilteredData,
  loading,
  setMode,
  setForecastRecipientId,
  forecastRecipientId,
  selectedProgram,
  setSelectedProgram,
  selectedArea,
  setSelectedArea,
  selectedUserType,
  setSelectedUserType,
  search,
  setSearch,
  defaultPageSize,
  setDefaultPageSize,
}) => {
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const userTypesDropdown = useSelector(selectUserTypes);
  const currentUser = useSelector(selectCurrentUser);
  const [showDeleteCfm, setShowDeleteCfm] = useState(false);

  const crud = new Crud('Hazard Forecast Contact List');

  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    let filtered = data;

    if (selectedProgram && selectedProgram.value !== '')
      filtered = filtered.filter(
        (x) =>
          x.RegularCondition?.ProgramIds?.includes(selectedProgram.value) ||
          x.TransitionCondition?.ProgramIds?.includes(selectedProgram.value),
      );

    if (selectedArea && selectedArea.value !== '')
      filtered = filtered.filter(
        (x) =>
          x.RegularCondition?.AvalancheAreaIds?.includes(selectedArea.value) ||
          x.TransitionCondition?.AvalancheAreaIds?.includes(selectedArea.value),
      );

    if (selectedUserType && selectedUserType.value !== '')
      filtered = filtered.filter((x) => x.UserTypeId === selectedUserType.value);

    if (!isValueEmpty(search)) {
      filtered = filtered.filter((x) => hasSearch(x.FirstName) || hasSearch(x.LastName) || hasSearch(x.Organization));
    }

    setFilteredData(filtered);
  }, [data, selectedProgram, selectedArea, selectedUserType, search]);

  const getFilteredArea = () => {
    if (selectedProgram && selectedProgram.value !== '') {
      return avalancheAreas
        .filter((a) => a.parentId === selectedProgram.value)
        .sort((a, b) => a.lastUpdated - b.lastUpdated);
    }

    return avalancheAreas;
  };

  const handleDelete = async () => {
    setShowDeleteCfm(false);

    const url = REST_API.FORECAST_RECIPIENTS + forecastRecipientId;

    const resp = await DeleteJson(url);
    if (resp.ok) {
      toast.success('Successfully deleted');
      const tempData = data.filter((x) => x.Id !== forecastRecipientId);
      setData([...tempData]);
    } else {
      toast.error('Error occurred while deleting');
    }
  };

  const actionMenu = (row) => {
    const userType = userTypesDropdown?.options.find((x) => x.value === row.row.original.UserTypeId);
    return (
      <>
        <div>
          {hasAccess([Permissions.WriteAvalancheForm]) && (
            <i
              className="fas fa-edit mx-1 my-1 awp-img-btn"
              title="Edit"
              onClick={() => {
                setMode(FORM_MODE.EDIT);
                setForecastRecipientId(row.row.original.Id);
              }}
            ></i>
          )}
          <i
            className="fas fa-eye mx-1 my-1 awp-img-btn"
            title="View"
            onClick={() => {
              setMode(FORM_MODE.VIEW);
              setForecastRecipientId(row.row.original.Id);
            }}
          ></i>
          {hasAccess([Permissions.WriteAvalancheForm]) && userType?.label === Usertypes.External && (
            <i
              className="fas fa-trash mx-1 my-1 awp-img-btn"
              title="Delete"
              onClick={() => {
                setForecastRecipientId(row.row.original.Id);
                setShowDeleteCfm(true);
              }}
            ></i>
          )}
        </div>
      </>
    );
  };

  const TableColumns = useMemo(
    () => [
      {
        Header: 'Last Name',
        accessor: 'LastName',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'First Name',
        accessor: 'FirstName',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Organization',
        accessor: 'Organization',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Last Updated',
        accessor: 'LastUpdated',
        sortType: sortByNumericThenAlpha,
        show: isLarge,
      },
      {
        Header: 'Last Updated By',
        accessor: 'LastUpdatedBy',
        sortType: sortByNumericThenAlpha,
        show: isLarge,
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );

  return (
    <>
      <div className="d-flex">{crud.getHeader()}</div>
      <Accordion defaultActiveKey="1">
        <Card border="light" className="page-card">
          <CardHeaderToggle as="h3" eventKey="0" title="Advanced Search" />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div className="row">
                <div className="col-sm-3">
                  <AwpDropdown
                    isMulti={false}
                    selectedValues={selectedProgram}
                    onChange={setSelectedProgram}
                    data={{
                      options: avalanchePrograms,
                      status: 'succeeded',
                    }}
                    title="Program"
                  />
                </div>
                <div className="col-sm-3">
                  <AwpDropdown
                    isMulti={false}
                    selectedValues={selectedArea}
                    onChange={setSelectedArea}
                    data={{
                      options: getFilteredArea(),
                      status: 'succeeded',
                    }}
                    title="Area"
                  />
                </div>
                <div className="col-sm-3">
                  <AwpDropdown
                    isMulti={false}
                    selectedValues={selectedUserType}
                    onChange={setSelectedUserType}
                    data={userTypesDropdown}
                    title="User Types"
                  />
                </div>
                <div className="col-sm-3">
                  <FormLabel>
                    <b>Search All</b>
                  </FormLabel>
                  <input
                    className="form-control"
                    placeholder="Search..."
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  ></input>
                </div>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      <Table
        columns={TableColumns}
        data={filteredData}
        fetchData={() => {}}
        loading={loading}
        search=""
        filters={[]}
        dataTriggers={[]}
        defaultSortBy={'LastName'}
        defaultPageSize={defaultPageSize}
        setDefaultPageSize={setDefaultPageSize}
      />
      <AwpYesNoModal
        showYesNoModal={showDeleteCfm}
        setShowYesNoModal={setShowDeleteCfm}
        onClickYes={handleDelete}
        title="Delete"
        body="Are you sure to delete the recipient?"
      />
      {hasAccess([Permissions.WriteAvalancheForm]) && (
        <div className="my-3">
          <Row>
            <Col sm={3} md={4}>
              <Button
                className="mr-2 my-3"
                variant="primary"
                onClick={() => {
                  setMode(FORM_MODE.CREATE);
                }}
                disabled={loading}
              >
                Add Recipient
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

ForecastRecipientTable.propTypes = {
  isMedium: PropTypes.bool,
  isLarge: PropTypes.bool,
  setData: PropTypes.func,
  data: PropTypes.array,
  filteredData: PropTypes.array,
  setFilteredData: PropTypes.func,
  loading: PropTypes.bool,
  setMode: PropTypes.func,
  setForecastRecipientId: PropTypes.func,
  forecastRecipientId: PropTypes.number,
  selectedProgram: PropTypes.object,
  setSelectedProgram: PropTypes.func,
  selectedArea: PropTypes.object,
  setSelectedArea: PropTypes.func,
  selectedUserType: PropTypes.object,
  setSelectedUserType: PropTypes.func,
  search: PropTypes.string,
  setSearch: PropTypes.func,
};

export default ForecastRecipientTable;
